import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate, useParams } from 'react-router-dom';
import GoBack from 'components/actions/GoBack';
import {
    MdDoneOutline,
    MdOutlinePayment,
    MdOutlineRocketLaunch,
} from 'react-icons/md';
import { getImage } from 'variables/images';
import OrderStatus from 'views/admin/main/ecommerce/orderDetails/components/OrderStatus';
import { TbTrash } from 'react-icons/tb';

const SellerOfferPage = () => {
    const [user, setUser] = useState<any>(null);
    const [sheetUrl, setSheetUrl] = useState(null);
    const [loadingSheet, setLoadingSheet] = useState(false);
    const [offer, setOffer] = useState<any>({});
    const nav = useNavigate();
    const params = useParams();

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                setUser(res.data);
                if (res.data && res.data.type !== 'seller') {
                    window.location.href = '/my-request';
                }
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    const getOffer = (id: string) => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/offer/${id}`, config)
            .then((res) => {
                setOffer(res.data);
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    const initSheetUrl = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/offer/seller-sheet/${params.id}`, config)
            .then((res) => {
                setSheetUrl(res.data.url);
            })
            .catch((err) => {
                console.log('url error', err);
            });
    };

    const openOrCreateSheet = async () => {
        setLoadingSheet(true);
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            };
            const response = await axios.post(
                `${CONFIG.apiUrl}/offer/seller-sheet/${params.id}/get-or-create`,
                {},
                config
            );
            if (response.data.sheetUrl) {
                setSheetUrl(response.data.sheetUrl);
                window.open(response.data.sheetUrl, '_blank');
                toast.success(
                    response.data.status === 'Sheet created successfully'
                        ? 'Google Sheet cree avec succes!'
                        : 'Google Sheet ouvert'
                );
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Erreur lors de l'acces a Google Sheet");
        } finally {
            setLoadingSheet(false);
        }
    };

    useEffect(() => {
        if (
            !localStorage.getItem('token') ||
            localStorage.getItem('token').length < 10
        )
            window.location.href = '/auth/sign-up';
        getOffer(params.id);
        initUser();
        initSheetUrl();
        // eslint-disable-next-line
    }, []);

    const InfoBox = ({ icon, title, description }: { icon?: any; title?: string; description?: string }) => (
        <div className="w-full rounded-xl border border-leadrs-stroke bg-leadrs-bg p-4">
            <div className="flex items-start gap-3">
                <span className="mt-0.5 text-leadrs-text-soft">{icon}</span>
                <div>
                    <h4 className="font-geist text-sm font-bold text-leadrs-text">{title}</h4>
                    <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{description}</p>
                </div>
            </div>
        </div>
    );

    const deleteOffer = (id: string) => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .delete(`${CONFIG.apiUrl}/seller-offer/${id}`, config)
            .then(() => {
                window.location.href = '/';
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    return (
        <div className="flex flex-col gap-4">
            <GoBack />

            <div className="relative rounded-xl border border-leadrs-stroke bg-white p-6">
                {user?._id === offer?.sellerId && (
                    <div className="absolute right-5 top-5">
                        <button
                            className="flex h-fit w-fit items-center rounded-lg border border-leadrs-stroke px-3 py-2 font-geist text-sm font-medium text-leadrs-red-focus transition hover:bg-leadrs-red-soft"
                            onClick={() => deleteOffer(offer._id)}
                        >
                            <TbTrash className="h-4 w-4" />
                        </button>
                    </div>
                )}

                <div className="flex gap-8">
                    <div className="hidden flex-shrink-0 lg:block">
                        {getImage(offer?.industry, offer?.subIndustry) ? (
                            <img
                                src={getImage(offer?.industry, offer?.subIndustry)}
                                className="mb-1 max-h-[400px] w-full rounded-xl object-cover"
                                alt={`${offer?.industry} ${offer?.subIndustry}`}
                            />
                        ) : (
                            <div className="flex h-[400px] w-[400px] items-center justify-center rounded-xl bg-leadrs-bg">
                                <p className="font-geist text-sm text-leadrs-text-soft">
                                    {offer?.industry} - {offer?.subIndustry}
                                </p>
                            </div>
                        )}
                        <div className="mt-4">
                            <OrderStatus offer={offer} />
                        </div>
                    </div>

                    <div className="flex-1">
                        <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">
                            {offer?.industry}
                        </p>
                        <h1 className="mt-1 font-crimson text-3xl font-semibold text-leadrs-text">
                            {offer?.subIndustry}
                        </h1>
                        <p className="mb-4 font-geist text-sm font-semibold text-leadrs-text-soft">
                            #{offer?.offerNumber}
                        </p>

                        <div className="mb-4 h-px w-full bg-leadrs-stroke" />

                        <div className="flex flex-col gap-3">
                            {offer?.status === 'done' && (
                                <InfoBox
                                    icon={<MdDoneOutline size={20} />}
                                    title="Mission terminee"
                                    description={`Vous avez genere ${offer?.leadsGenerated} leads (${(offer?.leadsGenerated * offer?.sellerPrice).toFixed(2)}€). Profitez de cette traction et cherchez un autre acheteur avec le meme besoin`}
                                />
                            )}
                            {offer?.status === 'running' && (
                                <InfoBox
                                    icon={<MdOutlineRocketLaunch size={20} />}
                                    title="Mission en cours"
                                    description={`Vous avez deja genere ${(offer?.leadsGenerated * offer?.sellerPrice).toFixed(2)}€ (${offer?.leadsGenerated} leads) pour cette mission. Le payement est effectue chaque semaine sur votre compte bancaire.`}
                                />
                            )}
                            {offer?.status === 'waiting_payment' && (
                                <>
                                    <InfoBox
                                        icon={<MdOutlinePayment size={20} />}
                                        title="En attente de paiement"
                                        description="Vous pourrez commencer votre campagne une fois la commande payee par l'acheteur. En attendant, si un lead est genere, nous nous assurons de vous le rediriger par mail."
                                    />
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="rounded-xl border border-leadrs-stroke bg-leadrs-bg p-4">
                                            <p className="font-geist text-xs text-leadrs-text-soft">Prix par lead</p>
                                            <p className="mt-1 font-crimson text-2xl font-bold text-leadrs-text">
                                                {offer?.sellerPrice || 0} €
                                            </p>
                                        </div>
                                        <div className="rounded-xl border border-leadrs-stroke bg-leadrs-yellow-soft p-4">
                                            <p className="font-geist text-xs text-leadrs-yellow-focus">Total de la commande</p>
                                            <p className="mt-1 font-crimson text-2xl font-bold text-leadrs-text">
                                                {((offer?.weeklyQuantity || 0) * (offer?.sellerPrice || 0)).toFixed(2)}€
                                            </p>
                                        </div>
                                    </div>
                                </>
                            )}
                            {!user?.isKYCVerified && (
                                <InfoBox
                                    icon={<MdOutlinePayment size={20} />}
                                    title="Coordonnees bancaires manquantes"
                                    description="Avant de vous positionner sur une offre, vous devez renseigner vos coordonnees bancaires pour recevoir vos paiements."
                                />
                            )}
                            {!user?.isValidated && (
                                <p className="font-geist text-sm text-leadrs-red-focus">
                                    Nous sommes en train de verifier votre compte. Vous ne pouvez pas encore postuler a une offre.
                                </p>
                            )}
                            {offer?.status === 'on_market' && user?.isKYCVerified && user?.isValidated && (
                                <div className="rounded-xl bg-leadrs-blue-soft px-4 py-3">
                                    <p className="font-geist text-sm font-medium text-leadrs-text">
                                        Votre offre est disponible sur la marketplace pour {offer?.sellerPrice}€ par lead.
                                    </p>
                                </div>
                            )}
                        </div>

                        {user?.canSellWithGSheet && (
                            <div className="mt-4">
                                <button
                                    onClick={openOrCreateSheet}
                                    disabled={loadingSheet}
                                    className="flex items-center gap-2 rounded-lg bg-leadrs-green-focus px-4 py-2 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {loadingSheet ? 'Chargement...' : sheetUrl ? 'Ouvrir mon Google Sheet' : 'Creer mon Google Sheet'}
                                </button>
                            </div>
                        )}

                        <button
                            onClick={() => nav(`/integration-help?offerId=${offer._id}`)}
                            className="mt-3 font-geist text-sm text-leadrs-brand underline"
                        >
                            Connecter a ma landing page
                        </button>

                        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Quantite</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.weeklyQuantity} leads</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Canaux d'acquisition</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.channels?.join(', ')}</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Besoin</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.type}</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Site web</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.websiteInfo}</p>
                            </div>
                        </div>

                        <div className="mt-4">
                            <p className="font-geist text-xs font-bold text-leadrs-text">Champs de donnees</p>
                            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.fields?.join(', ')}</p>
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Description</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.description}</p>
                            </div>
                            <div>
                                <p className="font-geist text-xs font-bold text-leadrs-text">Departements</p>
                                <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{offer?.regions?.join(', ')}</p>
                            </div>
                        </div>

                        {/* Financial Dashboard */}
                        {(offer?.status === 'running' || offer?.status === 'done') && (
                            <div className="mt-8">
                                <h3 className="mb-1 font-geist text-sm font-bold text-leadrs-text">Tableau de bord financier</h3>
                                <p className="mb-4 font-geist text-xs text-leadrs-text-soft">
                                    Suivez vos revenus et virements en temps reel
                                </p>

                                <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                                    <div className="rounded-xl border border-leadrs-stroke bg-leadrs-bg p-4">
                                        <p className="font-geist text-xs text-leadrs-text-soft">Leads generes</p>
                                        <p className="mt-1 font-crimson text-2xl font-bold text-leadrs-text">
                                            {offer?.leadsGenerated || 0}
                                        </p>
                                    </div>
                                    <div className="rounded-xl border border-leadrs-stroke bg-leadrs-bg p-4">
                                        <p className="font-geist text-xs text-leadrs-text-soft">Prix par lead</p>
                                        <p className="mt-1 font-crimson text-2xl font-bold text-leadrs-text">
                                            {offer?.sellerPrice || 0} €
                                        </p>
                                    </div>
                                    <div className="rounded-xl border border-leadrs-stroke bg-leadrs-yellow-soft p-4">
                                        <p className="font-geist text-xs text-leadrs-yellow-focus">Total reclamable</p>
                                        <p className="mt-1 font-crimson text-2xl font-bold text-leadrs-text">
                                            {((offer?.leadsGenerated || 0) * (offer?.sellerPrice || 0)).toFixed(2)}€
                                        </p>
                                    </div>
                                    <div className="rounded-xl border border-leadrs-stroke bg-leadrs-blue-soft p-4">
                                        <p className="font-geist text-xs text-leadrs-text-soft">Total deja verse</p>
                                        <p className="mt-1 font-crimson text-2xl font-bold text-leadrs-text">
                                            {(offer?.alreadyPaid || 0).toFixed(2)}€
                                        </p>
                                    </div>
                                </div>

                                <div className="rounded-xl bg-leadrs-brand p-5 text-white">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="font-geist text-sm font-semibold">Solde restant a recevoir</h4>
                                            <p className="mt-1 font-geist text-xs text-white/80">Montant en attente de virement</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-crimson text-3xl font-bold">
                                                {Math.max(0, (offer?.leadsGenerated || 0) * (offer?.sellerPrice || 0) - (offer?.alreadyPaid || 0)).toFixed(2)}€
                                            </p>
                                            <p className="font-geist text-xs text-white/80">
                                                {(offer?.leadsGenerated || 0) * (offer?.sellerPrice || 0) - (offer?.alreadyPaid || 0) > 0
                                                    ? 'Prochainement vire'
                                                    : 'Aucun montant en attente'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SellerOfferPage;
